import { addBallAction, sliceItemUpdate } from "..";
import { ActionType, FrameStencil, FrameStencils, MarkerType, StencilType } from "../../types";
import { addHandoff } from "./add-action/add-handoff";
import { addPlayerAction } from "./add-action/add-player-action";
import { addPossessionAction } from "./add-action/add-possession-action";
import { addBall } from "./add-marker/add-ball";
import { addPlayer } from "./add-marker/add-player";
import { clearAction } from "./clear-action";
import { clearMarker } from "./clear-marker";

/** ================================
 * Splice Marker Movement
 * Handle updating the marker movement for all operations on frame stencils, including adding, updating, and removing
 * of Markers and Actions
 ================================ */
export interface FrameStencilControllerProps {
  stencils: FrameStencil[];
  operation: "ADD" | "UPDATE" | "REMOVE";
  stencil: FrameStencil;
}
export const frameStencilController = (props: FrameStencilControllerProps): FrameStencils => {
  const { operation, stencil } = props;

  const { stencilType } = stencil;
  const stencils = props.stencils || [];

  switch (stencilType) {
    /** ================================
     * ACTION
     ================================ */
    case StencilType.ACTION: {
      switch (operation) {
        /** ================================
         * ADD
         ================================ */
        case "ADD": {
          switch (stencil.actionType) {
            case ActionType.PLAYER:
              return addPlayerAction({ action: stencil, stencils });

            case ActionType.BALL:
              return addBallAction({ action: stencil, stencils });

            case ActionType.POSSESSION:
              return addPossessionAction({ action: stencil, stencils });

            case ActionType.HANDOFF:
              return addHandoff({ action: stencil, stencils });

            default:
              return stencils;
          }
        }

        /** ================================
         * UPDATE
         ================================ */
        case "UPDATE": {
          const { clearedAction, clearedStencils } = clearAction({ action: stencil, stencils });
          switch (clearedAction.actionType) {
            case ActionType.PLAYER:
              return addPlayerAction({ action: clearedAction, stencils: clearedStencils });
            case ActionType.BALL:
              return addBallAction({ action: clearedAction, stencils: clearedStencils });
            case ActionType.POSSESSION:
              return addPossessionAction({ action: clearedAction, stencils: clearedStencils });
            case ActionType.HANDOFF:
              return addHandoff({ action: clearedAction, stencils: clearedStencils });
            default:
              return sliceItemUpdate({ item: clearedAction, arr: clearedStencils });
          }
        }

        /** ================================
         * REMOVE
         ================================ */
        case "REMOVE": {
          const { clearedStencils } = clearAction({ action: stencil, stencils });
          return clearedStencils;
        }

        default:
          return stencils;
      }
    }

    /** ================================
     * MARKER
     ================================ */
    case StencilType.MARKER: {
      switch (operation) {
        /** ================================
         * ADD
         ================================ */
        case "ADD": {
          switch (stencil.markerType) {
            case MarkerType.PLAYER:
              return addPlayer({ player: stencil, stencils });

            case MarkerType.BALL:
              return addBall({ ball: stencil, stencils });

            default:
              return [...stencils, stencil];
          }
        }

        /** ================================
         * UPDATE
         ================================ */
        case "UPDATE": {
          const { clearedMarker, clearedStencils } = clearMarker({ marker: stencil, stencils });

          switch (clearedMarker.markerType) {
            case MarkerType.PLAYER:
              return addPlayer({ player: clearedMarker, stencils: clearedStencils });

            case MarkerType.BALL:
              return addBall({ ball: clearedMarker, stencils: clearedStencils });

            default:
              //return sliceItemUpdate({ item: clearedMarker, arr: clearedStencils }); keeping cause i'm not sure what this was supposed to do
              return [clearedMarker, ...clearedStencils];
          }
        }

        /** ================================
         * REMOVE
         ================================ */
        case "REMOVE": {
          const { clearedStencils } = clearMarker({ marker: stencil, stencils });
          return clearedStencils;
        }

        default:
          return stencils;
      }
    }

    default:
      return stencils;
  }
};
